<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('ZONES')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.timeZoneCode"
                    :items="timeZonesItemsFilter"
                    :loading="timeZonesFilter.isLoading"
                    :search-input.sync="timeZonesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('zones.timeZone')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="timeZonesFilter.isEdit = true"
                    @change="timeZonesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.agencyCode"
                    :label="$t('zones.agency')"
                    v-if="!$userIsAdmin()"
                    disabled
                  ></v-text-field>
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    :items="agenciesItemsFilter"
                    v-if="$userIsAdmin()"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('zones.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/zones"
              :filter="this.filter"
              :filterExport="filterExport"
            />
            <ImportExcel
              url="/zones"
              @done="search"
              v-if="$checkPermission('ZONES')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('ZONES')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code, item.agency.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('ZONES')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code, item.agency.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('ZONES')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code, item.agency.code)"
                  ></crud-button>
                </td>
                <td>{{ item.code }}</td>
                <td>{{ item.name }}</td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agency.code }}</span>
                    </template>
                    <span>{{ item.agency.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.timeZone.code }}</span>
                    </template>
                    <span>{{ item.timeZone.name }}</span>
                  </v-tooltip>
                </td>
                <td>{{ item.offsetDays }}</td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('zones.code')"
                    rules="required|max:16"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('zones.code')"
                      v-model="item.code"
                      :disabled="editMode"
                      required
                      :error-messages="errors"
                      counter="16"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('zones.name')"
                    rules="required|max:60"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('zones.name')"
                      v-model="item.name"
                      required
                      :error-messages="errors"
                      counter="60"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('zones.agency')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="item.agencyCode"
                      :label="$t('zones.agency')"
                      v-if="!$userIsAdmin()"
                      disabled
                    ></v-text-field>
                    <v-autocomplete
                      v-model="item.agencyCode"
                      v-if="$userIsAdmin()"
                      :items="agenciesItems"
                      :loading="agencies.isLoading"
                      :search-input.sync="agencies.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('zones.agency')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      @focus="agencies.isEdit = true"
                      @change="agencies.isEdit = false"
                      :error-messages="errors"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('zones.agency')"
                    v-model="item.agencyString"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('zones.timeZone')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.timeZoneCode"
                      :items="timeZonesItems"
                      :loading="timeZones.isLoading"
                      :search-input.sync="timeZones.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('zones.timeZone')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      @focus="timeZones.isEdit = true"
                      @change="timeZones.isEdit = false"
                      :error-messages="errors"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('zones.offsetDays')"
                    rules="required|max:100"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('zones.offsetDays')"
                      v-model="item.offsetDays"
                      required
                      :error-messages="errors"
                      @keypress="integerOnlyField($event)"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";

const CONTEXT = "/zones";
const CONTEXT_AGENCIES = "/agencies";
const CONTEXT_TIMEZONES = "/timeZones";
const ENTITY_NAME = "zone.";

export default {
  components: {
    CrudButton,
    ExportExcel,
    ImportExcel
  },
  name: "Zone",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    isAdmin: false,
    dialog: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      agencyCode: "",
      timeZoneCode: "",
      deletedItems: "ALL"
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    agencies: {
      isLoading: false,
      isLoadingWaiting: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      isLoadingWaiting: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    timeZones: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    timeZonesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("zones.title.edit")
        : this.$t("zones.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        { text: this.$t("zones.datatable.code"), value: ENTITY_NAME + "code" },
        { text: this.$t("zones.datatable.name"), value: ENTITY_NAME + "name" },
        {
          text: this.$t("zones.datatable.agency"),
          value: ENTITY_NAME + "agencyCode"
        },
        {
          text: this.$t("zones.datatable.timeZone"),
          value: ENTITY_NAME + "timeZoneCode"
        },
        {
          text: this.$t("routes.datatable.offsetDays"),
          value: ENTITY_NAME + "offsetDays"
        }
      ];
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItemsFilter() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    timeZonesItems() {
      return this.timeZones.entries.map(entry => {
        const description = entry.code + " - " + entry.description;
        return Object.assign({}, entry, { description });
      });
    },
    timeZonesItemsFilter() {
      return this.timeZonesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.description;
        return Object.assign({}, entry, { description });
      });
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "agencies.search": function(value) {
      console.debug("agencies.search");
      this.preLoadGenericSearchEntity(value, this.agencies, CONTEXT_AGENCIES);
    },
    "agenciesFilter.search": function(value) {
      console.debug("agenciesFilter.search");
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES
      );
    },
    "timeZones.search": function(value) {
      console.debug("timeZones.search");
      this.preLoadGenericSearchEntity(value, this.timeZones, CONTEXT_TIMEZONES);
    },
    "timeZonesFilter.search": function(value) {
      console.debug("timeZones.search");
      this.preLoadGenericSearchEntity(
        value,
        this.timeZonesFilter,
        CONTEXT_TIMEZONES
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.item.agencyCode = this.$userIsAdmin()
        ? ""
        : this.$keycloak.tokenParsed.agency;
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");
      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id, agency) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + agency + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.agencyString = item.agency.code + " - " + item.agency.name;
      setTimeout(() => {
        this.timeZones.search =
          item.timeZone.code + " - " + item.timeZone.description;
      }, 500);
    },
    async update() {
      console.debug("Update form");
      localhttp
        .put(
          CONTEXT + "/update/" + this.item.agency.code + "/" + this.item.code,
          this.item
        )
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id, agency) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + agency + "/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id, agency) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + agency + "/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetAgenciesSearch();
      this.resetTimeZonesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetAgenciesSearch() {
      console.debug("resetAgenciesSearch");
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    resetTimeZonesSearch() {
      console.debug("resetTimeZonesSearch");
      this.timeZones.search = "";
      this.timeZones.entries = [];
    },
    filterExport(jsonData) {
      jsonData.forEach(function(v) {
        delete v.agency;
        delete v.timeZone;
      });
    }
  },
  beforeMount() {
    this.filter.agencyCode = this.$userIsAdmin()
      ? ""
      : this.$keycloak.tokenParsed.agency;
  }
};
</script>
